import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import { Card, CircularProgress } from "@mui/material";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import PieChart from "layouts/area-dashboard/components/Totals/PieChart";
import VerticalStackedBarChart from "layouts/area-dashboard/components/Totals/VerticalStackedBarChart";
import DynamicSiteCard from "layouts/area-dashboard/components/SiteCard";
import AreaPpmSummary from "layouts/area-dashboard/components/AreaPpmSummary";
import useAuth from "../../useAuth";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import LineChart from "examples/Charts/LineCharts/LineChart";
import { lineChartOptionsDashboard } from "layouts/area-dashboard/data/lineChartOptions";

function AreaDashboard() {
  useAuth();
  const navigate = useNavigate();
  const { region_id } = useParams();

  const [regionSites, setRegionSites] = useState([]);
  const [lineChartData, setLineChartData] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const userToken = localStorage.getItem("token");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch region sites
        const sitesResponse = await axios.get(
          "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/region/sites",
          {
            headers: { Authorization: `${userToken}`, "Content-Type": "application/json" },
            params: { region_id },
          }
        );

        const sites = sitesResponse.data;

        // Fetch all gateway profiles in parallel
        const gatewayProfiles = await Promise.all(
          sites.map(async (site) => {
            try {
              const gatewayResponse = await fetch(
                `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/gateway/profile?site_id=${site.site_id}`,
                { headers: { Authorization: `${userToken}` } }
              );
              const response = await gatewayResponse.json();
              const gatewayName = response[0]?.name || null;
              return { site, gatewayName };
            } catch (error) {
              console.error(`Error fetching gateway profile for site ${site.site_id}:`, error.message);
              return { site, gatewayName: null };
            }
          })
        );

        const gatewayNames = gatewayProfiles
          .map((g) => g.gatewayName)
          .filter((name) => name !== null);

        if (gatewayNames.length === 0) {
          setRegionSites([]);
          setLineChartData([]);
          setLoading(false);
          return;
        }

        // Fetch batched Timestream data for all gateways
        const lambdaResponse = await axios.get(
          "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/query-timestream",
          {
            headers: { Authorization: `${userToken}` },
            params: {
              thing_names: gatewayNames.join(","), 
              query_type: "area_dashboard",
            },
          }
        );

        const siteDataMap = lambdaResponse.data;

        // Merge site + gateway + Timestream data
        const enrichedSites = gatewayProfiles.map(({ site, gatewayName }) => {
          const data = siteDataMap[gatewayName] || {};
          const { daily = [], rap_total_month = Array(12).fill(0), bit_total_month = Array(12).fill(0) } = data;

          return {
            id: site.site_id,
            name: site.name,
            gatewayName,
            Asphalt_Day: parseFloat(daily[0] || 0),
            RAP_Total_Day: parseFloat(daily[1] || 0),
            Bit_Total: parseFloat(daily[2] || 0),
            Fuel_Total: parseFloat(daily[3] || 0),
            RAP_Total: parseFloat(daily[4] || 0),
            Asphalt_Year: parseFloat(daily[5] || 0),
            Breakdown_Hrs: parseFloat(daily[6] || 0),
            Offline_Hrs: parseFloat(daily[7] || 0),
            Idle_Hrs: parseFloat(daily[8] || 0),
            Production_Hrs: parseFloat(daily[9] || 0),
            Electric_Total: parseFloat(daily[10] || 0),
            Gas_Total: parseFloat(daily[11] || 0),
            RAP_Total_Month: rap_total_month,
            Bit_Total_Month: bit_total_month,
          };
        });

        setRegionSites(enrichedSites);

        // Prepare chart data
        const chartData = enrichedSites.map((site) => ({
          name: site.name,
          data: site.Bit_Total_Month || Array(12).fill(0),
        }));
        setLineChartData(chartData);
      } catch (error) {
        console.error("Error fetching sites or data:", error.message);
        setError("An error occurred while fetching data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    const interval = setInterval(() => {
      fetchData();
    }, 1000000);

    return () => clearInterval(interval);
  }, [userToken, region_id]);

  const handleCardClick = () => {
    navigate(`/ppm/section-list`);
  };

  return (
    <DashboardLayout>
      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 9999,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress color="inherit" />
        </div>
      )}
      <div style={{ zIndex: 10000, position: "relative" }}>
        <DashboardNavbar />
      </div>
      <div style={{ filter: loading ? "blur(5px)" : "none", transition: "filter 0.3s ease" }}>
        <VuiBox>
          <Grid container spacing={0.5} mb={0.5}>
            <Grid item xs={12} sm={6} md={6} lg={3}>
              <Card sx={{ height: "100%" }}>
                <VuiBox sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                  <VuiTypography variant="h6" color="white">
                    Asphalt
                  </VuiTypography>
                  <PieChart
                    data={
                      regionSites.length > 0
                        ? regionSites.map((site) => ({
                            name: site.name,
                            Asphalt_Year: site.Asphalt_Year || 0,
                          }))
                        : [{ name: "No Data", Asphalt_Year: 0 }]
                    }
                  />
                </VuiBox>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={6} lg={3}>
              <Card sx={{ height: "100%" }}>
                <VuiBox
                  sx={{
                    height: "300px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <VuiTypography variant="h6" color="white">
                    RAP
                  </VuiTypography>
                  <VuiBox sx={{ flexGrow: 1 }}>
                    <VerticalStackedBarChart
                      siteData={
                        regionSites.length > 0
                          ? regionSites.map((site) => ({
                              name: site.name,
                              RAP_Total_Month: site.RAP_Total_Month || Array(12).fill(0),
                            }))
                          : [{ name: "No Data", RAP_Total_Month: Array(12).fill(0) }]
                      }
                    />
                  </VuiBox>
                </VuiBox>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={6} lg={3}>
              <Card sx={{ height: "100%" }}>
                <VuiBox
                  sx={{
                    height: "370px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <VuiTypography variant="h6" color="white">
                    Binder
                  </VuiTypography>
                  <VuiBox sx={{ flexGrow: 1 }}>
                    <LineChart
                      lineChartData={lineChartData}
                      lineChartOptions={lineChartOptionsDashboard}
                    />
                  </VuiBox>
                </VuiBox>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={6} lg={3}>
              <Card sx={{ height: "100%" }}>
                <VuiBox sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                  <VuiTypography variant="h6" color="white">
                    PPM
                  </VuiTypography>
                  <AreaPpmSummary userSites={regionSites || []} />
                </VuiBox>
              </Card>
            </Grid>
          </Grid>

          <VuiBox mt={0.5}>
            <Grid container spacing={0.5}>
              {regionSites.map((site, index) => {
                const isLast = index === regionSites.length - 1;
                const isOnlyInRowLg =
                  regionSites.length % 3 === 1 && index === regionSites.length - 1;
                const isOnlyInRowMd =
                  regionSites.length % 2 === 1 && index === regionSites.length - 1;

                return (
                  <Grid
                    item
                    xs={12}
                    sm={isOnlyInRowMd ? 12 : 6}
                    md={isOnlyInRowMd ? 12 : 6}
                    lg={isOnlyInRowLg ? 12 : 4}
                    key={site.id}
                  >
                    <DynamicSiteCard site={site} />
                  </Grid>
                );
              })}
            </Grid>
          </VuiBox>
        </VuiBox>
        <Footer />
      </div>
    </DashboardLayout>
  );
}

export default AreaDashboard;
